import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { GoogleChartInterface } from 'ng2-google-charts';
import { DataItemDiagComponent } from 'src/app/common/entity/data-item-diag/data-item-diag.component';
import { ConfQuoteLine, ProductionLine, Sprint } from 'src/app/model';
import { ProjectService } from 'src/app/services/project.service';
import { LayoutService } from '../../../../../../services/layout.service';
import { WorkstationService } from 'src/app/services/workstation.service';
import { EntityService } from 'src/app/services/entity.service';
import { WorkstationLine } from 'src/app/models/workstationline';
import { Entdata } from 'src/app/model';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';

@Component({
  selector: 'app-prod-schedule-sprint',
  templateUrl: './prod-schedule-sprint.component.html',
  styleUrls: ['./prod-schedule-sprint.component.css'],
})
export class ProdScheduleSprintComponent implements OnInit {
  @Input() SPRINT: Sprint;
  @Input() SPRINTS: Sprint[] = [];
  @Input() SCHEDULEID: number = 0;
  @Output() refreshData = new EventEmitter();
  public burnDownChartExpand: boolean = false;
  public chart: GoogleChartInterface;
  dragedOrder: any;
  isHandset = this.layoutService.isHandset;
  workstationLinesMap: { [orderId: number]: WorkstationLine[] } = {};
  workstationTitles: { [workstationId: number]: string } = {};
  uniqueWorkstationIds: number[] = [];

  constructor(
    public dialog: MatDialog,
    private readonly projectService: ProjectService,
    private readonly layoutService: LayoutService,
    private readonly workstationService: WorkstationService,
    private readonly entityService: EntityService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeData();
  }

  public onChartReady(event: any): void {
    console.log('Chart is ready:', event);
  }

  private initializeData(): void {
    this.fetchWorkstationLines()
      .pipe(tap(() => this.initializeChart()))
      .subscribe();
  }

  private fetchWorkstationLines(): Observable<void> {
    if (!this.SPRINT.orders || this.SPRINT.orders.length === 0) {
      return new Observable<void>((observer) => {
        observer.next();
        observer.complete();
      });
    }

    return new Observable<void>((observer) => {
      this.SPRINT.orders!.forEach((order, index) => {
        this.workstationService.getSavedLines(order.id).subscribe((lines) => {
          this.workstationLinesMap[order.id] = lines;
          lines.forEach((line) => {
            const wsId = line.workstation_id;
            const wsName =
              line.workstation?.workstation_name || `Workstation ${wsId}`;
            this.workstationTitles[wsId] = wsName;
          });

          if (index === this.SPRINT.orders!.length - 1) {
            const allIds = Object.keys(this.workstationTitles).map(Number);
            this.uniqueWorkstationIds = Array.from(new Set(allIds)).sort(
              (a, b) => a - b
            );
            observer.next();
            observer.complete();
          }
        });
      });
    });
  }

  private initializeChart(): void {
    this.getProductionLines().subscribe((lines) => {
      this.chart = {
        chartType: 'BarChart',
        dataTable: this.fillChart(lines),
        firstRowIsData: false,
        options: {
          chart: {},
          vAxis: { title: 'Workstation', textStyle: { fontSize: 12 } },
          hAxis: { title: 'Minutes', format: '#,###' },
          seriesType: 'bars',
          height: 400,
          width: 500,
        },
      };
      this.chart.options.hAxis.format = this.chart.options.hAxis.format.replace(
        ',',
        '.'
      );
      this.cdr.detectChanges();
    });
  }

  private getProductionLines(): Observable<ProductionLine[]> {
    const lines: ProductionLine[] = [];
    if (!this.SPRINT.orders || this.SPRINT.orders.length === 0) {
      return new Observable<ProductionLine[]>((observer) => {
        observer.next(lines);
        observer.complete();
      });
    }

    this.SPRINT.orders.forEach((order) => {
      const workstationLines = this.workstationLinesMap[order.id] || [];

      workstationLines.forEach((wsLine) => {
        const lineId = `#${wsLine.workstation_id}`;
        const lineName =
          this.workstationTitles[wsLine.workstation_id] ||
          `Workstation ${wsLine.workstation_id}`;
        const lineTime = wsLine.minutes;

        const existingLine = lines.find((x) => x.production_line_id === lineId);
        if (existingLine) {
          existingLine.time += lineTime;
        } else {
          const newLine = new ProductionLine();
          newLine.production_line_id = lineId;
          newLine.name = lineName;
          newLine.time = lineTime;
          lines.push(newLine);
        }
      });

      if (workstationLines.length === 0) {
        const defaultLineId = '#default';
        const existingLine = lines.find(
          (x) => x.production_line_id === defaultLineId
        );
        const newLine = new ProductionLine();
        newLine.production_line_id = defaultLineId;
        newLine.name = 'Unknown Workstation';
        lines.push(newLine);
      }
    });

    return new Observable<ProductionLine[]>((observer) => {
      observer.next(lines);
      observer.complete();
    });
  }

  private fillChart(lines: ProductionLine[]): any[] {
    const arr: any[] = [['Workstation', 'Time']];

    for (const line of lines) {
      arr.push([line.name, line.time]);
    }

    if (lines.length === 0) {
      arr.push(['-', 0]);
    }

    return arr;
  }

  public getWorkstationLines(orderId: number): WorkstationLine[] {
    return this.workstationLinesMap[orderId] || [];
  }

  public openTask(order: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      itemId: order.id,
      entityCode: 'productionorder',
    };
    this.dialog
      .open(DataItemDiagComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        this.refreshData.emit();
      });
  }

  public moveOrderToSprint(sprint: Sprint, order) {
    this.projectService
      .addTaskToSprint(this.SCHEDULEID, sprint, order)
      .pipe(take(1))
      .subscribe((res) => {
        if (res.success) {
          this.refreshData.emit();
        }
      });
  }
}
